import { getFridgeId } from "@jidlomat/terminal-core/utils/browser/localStorage";
import { log } from "@jidlomat/terminal-core/utils/logger";
import type { Event } from "@sentry/react";
import * as Sentry from "@sentry/react";
import type { RootOptions } from "react-dom/client";
import { Config } from "../configuration/eatSmartConfig";

// --- Event readability normalization -------------------------------------------------------------
// Most "issues" in this project are not distinct bugs: the same logical event fragments into hundreds
// of issues because (a) dynamic fridge UUIDs are baked into the message string, (b) Sentry's default
// grouping folds in the in-flight GraphQL operation, and (c) the captureConsole fallback re-captures
// our own logs with an "[APP] [LEVEL]" prefix. normalizeSentryEvent (wired as beforeSend) rewrites the
// title to a stable, id-free form and pins a fingerprint for the known noisy clusters, so each one
// collapses to a single issue. Per-fridge visibility is preserved via the `fridgeId` tag, not the title.

// A v1–v5 UUID (covers fridge ids and dish-type ids alike).
const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/giu;
// The "[APP] [WARNING] " style prefix our logger adds on the console fallback path.
const APP_PREFIX_RE = /^\[APP\] \[(?:ERROR|WARNING|INFO|DEBUG)\]\s*/u;
// A short, non-UUID id that follows "fridge:" (e.g. the truncated "78215" / "6e29b" in modal logs).
const FRIDGE_TOKEN_RE = /(\bfridge:\s*)[0-9a-z]+/giu;
// An object accidentally string-concatenated into the message.
const OBJECT_OBJECT_RE = /\s*\[object Object\]\s*$/u;

const normalizeTitle = (raw: string): string =>
	raw
		.replace(APP_PREFIX_RE, "")
		.replace(UUID_RE, "<id>")
		.replace(FRIDGE_TOKEN_RE, "$1<id>")
		.replace(OBJECT_OBJECT_RE, "")
		.trim();

// Stable fingerprints for the recurring clusters, matched against the normalized title. First match
// wins, so list more specific patterns first. Anything not matched keeps Sentry's default grouping,
// so genuinely new errors still surface as their own issues.
const CLUSTER_RULES: ReadonlyArray<readonly [RegExp, string]> = [
	[/^Refetching prices:/u, "price-refetch"],
	[/^Response from price refetch/u, "price-refetch-response"],
	[/^Setting initial data after broken subscription/u, "subscription-recovery-set-data"],
	[/^Sending initial query after broken subscription/u, "subscription-recovery-send-query"],
	[/^Sending initial query after a boot without a state/u, "boot-retry-send-query"],
	[/^Initial state arrived after a boot without one/u, "boot-retry-recovered"],
	[/^Subscription restored/u, "subscription-restored"],
	[/^Door opened without order/u, "door-opened-without-order"],
	[/^Successfully fetched \d+ unknown dish types/u, "dish-types-fetched"],
	[/^Added \d+ valid dish types out of \d+ fetched/u, "dish-types-added"],
	[/^Fetching unknown dish types on the fly/u, "dish-types-fetching"],
	[/^Filtered out \d+ incomplete dish types/u, "dish-types-filtered"],
	[/^Error sending cloudwatch metric/u, "cloudwatch-metric-error"],
	[/HTML5 Audio pool exhausted/u, "audio-pool-exhausted"],
	[/^Starting up modal displayed/u, "modal-starting-up"],
	[/^Empty fridge modal displayed/u, "modal-empty-fridge"],
	[/^Error modal displayed/u, "modal-error"],
	[/^Restricted modal displayed/u, "modal-restricted"],
	[/^Out of service modal displayed/u, "modal-out-of-service"],
	[/^Remote refill modal displayed/u, "modal-remote-refill"],
	[/^QR code is null/u, "qr-null-refetch"],
	[/Missing QR code on fridge/u, "qr-missing"],
	[/^QR lifecycle: transaction end/u, "qr-lifecycle-transaction-end"],
	[/^QR unchanged after transaction/u, "qr-unchanged-refetch"],
	[/^Refill started on fridge/u, "refill-started"],
	[/^Refill finished on fridge/u, "refill-finished"],
	[/Could not ping Google DNS/u, "dns-offline"],
	// Ends with what the import was rejected with, which may be "Load failed" or name a chunk by the
	// hash of its build: before the rule below, and one issue for all of them.
	[/^Flow chunk failed to load/u, "flow-chunk-failed"],
	[/Load failed/u, "network-load-failed"],
];

const clusterFingerprint = (title: string): string | undefined =>
	CLUSTER_RULES.find(([pattern]) => pattern.test(title))?.[1];

export const normalizeSentryEvent = <T extends Event>(event: T): T => {
	if (typeof event.message === "string") {
		event.message = normalizeTitle(event.message);
	}
	if (event.logentry && typeof event.logentry.message === "string") {
		event.logentry.message = normalizeTitle(event.logentry.message);
	}
	for (const value of event.exception?.values ?? []) {
		if (typeof value.value === "string") {
			value.value = normalizeTitle(value.value);
		}
	}

	const title = event.message ?? event.logentry?.message ?? event.exception?.values?.[0]?.value ?? "";
	const fingerprint = clusterFingerprint(title);
	if (fingerprint) {
		event.fingerprint = [fingerprint];
	}

	return event;
};

/**
 * The options of the React root (main.tsx). React hands an error that a boundary caught to
 * `onCaughtError` before the boundary hears of it, and by default writes it to console.error. The
 * console integration below would capture it there, without the component stack, and Sentry would
 * then drop the report of the boundary, with its name and the component stack, as an error it has
 * seen; a thrown value that is no Error, which Sentry cannot mark as seen, would come twice. Every
 * boundary of the app reports what it catches, so React's own report goes to the console as a
 * warning, which the integration does not capture. Everything else logged as an error it still does.
 */
export const reactRootOptions: RootOptions = {
	onCaughtError: (error, errorInfo) => {
		log("A boundary caught an error while the page was drawn", "warning", error, errorInfo.componentStack);
	},
};

export const initSentry = () => {
	if (window.MSW_ENABLED) {
		return;
	}

	// The scope below is filled from storage. Reading the fridge id first copies a `?fridgeId=` of the
	// URL into storage (and asks for an id when there is none). Without it a tablet that was just set
	// up, or pointed at another fridge or origin by its URL, would report its first session without a
	// fridge or with the previous one.
	getFridgeId();

	Sentry.init({
		// when undefined, sentry does not send any events
		dsn: Config.tools.sentryDSN,
		environment: import.meta.env.MODE,
		normalizeDepth: 6,
		release: `terminal-app@${import.meta.env.APP_VERSION}+${import.meta.env.VITE_BUILD_NUMBER}`,
		transport: Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport),
		beforeSend: normalizeSentryEvent,
		integrations: [
			Sentry.browserTracingIntegration(),
			// "warn" is deliberately excluded: it re-captured our own console-fallback logs (the
			// "[APP] [WARNING]" duplicates) and third-party noise (Howler's "HTML5 Audio pool exhausted").
			Sentry.captureConsoleIntegration({
				levels: ["error"],
			}),
		],
		initialScope: {
			tags: {
				fridgeId: localStorage.getItem("fridgeId"),
				tabletType: localStorage.getItem("tabletType"),
			},
			contexts: {
				fridgeInfo: {
					fridgeId: localStorage.getItem("fridgeId"),
					tabletType: localStorage.getItem("tabletType"),
				},
			},
		},
		// Performance Monitoring
		tracesSampleRate: 1.0, //  Capture 100% of the transactions
		// Set 'tracePropagationTargets' to control for which
		// URLs distributed tracing should be enabled
		tracePropagationTargets: ["localhost"],
	});
};
